"use client"

import { forwardRef, useEffect, useRef, useImperativeHandle } from "react"
import Hls from "hls.js"

export type VideoFields = {
  id: string | null | undefined
  bestAvailableVideoFile: string | null | undefined
}

interface Props extends React.VideoHTMLAttributes<HTMLVideoElement> {
  onPosterLoad?: () => void
}

export interface VideoHandle {
  play: () => Promise<void>
  pause: () => void
}

export const VideoElement = forwardRef<VideoHandle, Props>(
  ({ src, className, poster, controls = true, onPosterLoad, ...props }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null)

    useImperativeHandle(ref, () => ({
      play: () => videoRef.current?.play() ?? Promise.resolve(),
      pause: () => videoRef.current?.pause(),
    }))

    useEffect(() => {
      if (!src || !videoRef?.current || typeof src !== "string") return

      const hls = new Hls({ debug: true })

      if (Hls.isSupported()) {
        hls.attachMedia(videoRef.current)
        hls.loadSource(src)
      } else if (videoRef.current.canPlayType("application/vnd.apple.mpegURL")) {
        // Native HLS support
        videoRef.current.src = src
      }

      return () => {
        hls.destroy()
      }
    }, [src])

    return (
      <video
        ref={videoRef}
        poster={poster}
        onLoad={() => onPosterLoad?.()}
        controls={controls}
        preload="metadata"
        className={className}
        {...props}
      />
    )
  }
)

VideoElement.displayName = "VideoElement"
